import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';
import { SEO } from '../components/SEO';

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="20 May 2026">
      <SEO
        title="Privacy Policy — Dormant Accounts Filing"
        description="How Dormant Accounts Filing collects, uses, and protects your data when you file dormant company accounts with us."
        path="/privacy"
      />
      <p>
        This policy explains what personal information we collect when you use Dormant Accounts
        Filing, why we collect it, how we handle it, and what your rights are. We've tried to
        write it in plain English – no legalese for its own sake.
      </p>

      <h2>1. Who we are</h2>
      <p>
        Dormant Accounts Filing is a service that lets UK private limited companies submit
        dormant company accounts (form AA02) directly to Companies House via their XML Gateway.
        We are the data controller for the information you provide to us.
      </p>
      <p>
        Contact:{' '}
        <a href="mailto:contact.dormantfiling@gmail.com">contact.dormantfiling@gmail.com</a>.
      </p>

      <h2>2. What we collect</h2>
      <p>To file your accounts we need a small amount of information:</p>
      <ul>
        <li>
          <strong>Your email address</strong> – so we can send you payment receipts and the
          Companies House filing confirmation.
        </li>
        <li>
          <strong>Company details</strong> – company name, Companies House registration number,
          accounting period start and end dates. Most of this we fetch from Companies House
          based on your company number.
        </li>
        <li>
          <strong>Director name</strong> – the person approving the accounts (appears on the
          filing as the signatory).
        </li>
        <li>
          <strong>Balance-sheet figures</strong> – share capital, current assets, creditors etc.
          for the accounting period.
        </li>
        <li>
          <strong>Companies House authentication code</strong> – the 6-character code that
          authorises filings for your company. See section 4 for exactly how this is handled.
        </li>
        <li>
          <strong>Payment information</strong> – processed by Stripe. We never see or store
          your card details; Stripe gives us a token representing the payment.
        </li>
      </ul>

      <h2>3. How we use it</h2>
      <ul>
        <li>To prepare and submit your dormant accounts filing to Companies House.</li>
        <li>To take payment for the filing.</li>
        <li>To email you a payment receipt and a Companies House filing confirmation.</li>
        <li>To respond to any support enquiries you send us.</li>
        <li>
          To send a single reminder around 30 days before your next accounts are due – you can
          opt out of this any time by emailing us.
        </li>
      </ul>
      <p>
        We don't use your data for marketing, profiling, advertising, or selling on to third
        parties.
      </p>

      <h2>4. How we handle your authentication code</h2>
      <p>
        Your Companies House authentication code is the most sensitive thing you give us, and we
        treat it accordingly.
      </p>
      <ul>
        <li>
          The code is <strong>encrypted at the moment you submit it</strong> and held only in
          our server's memory, never written to a database, log file, backup, or any other
          persistent storage.
        </li>
        <li>
          It is used <strong>once</strong> – to authenticate the submission to the Companies
          House XML Gateway – and then <strong>deleted immediately</strong>.
        </li>
        <li>
          We don't share it with any third party other than Companies House itself.
        </li>
        <li>
          If your submission fails (for example, you accidentally typed the wrong code) and you
          ask us to retry, we will need you to re-enter the code. We deliberately don't keep it
          around for "convenience".
        </li>
        <li>
          We cannot recover or look up an authentication code on your behalf. If you've lost it,
          you need to request a new one from Companies House – it's posted to your registered
          office address (see{' '}
          <a
            href="https://www.gov.uk/guidance/company-authentication-codes-for-online-filing"
            target="_blank"
            rel="noreferrer"
          >
            this guide
          </a>
          ).
        </li>
      </ul>

      <h2>5. Where your data is stored</h2>
      <p>
        Customer order data is stored in an encrypted PostgreSQL database hosted in the
        European Union region. Connections to the database are encrypted in transit (TLS), and
        Row-Level Security is enabled at the database level so that the data is not exposed
        via any public API.
      </p>
      <p>
        Generated iXBRL filing documents (the financial accounts in XML form) are stored
        alongside the order so we can re-submit if needed.
      </p>

      <h2>6. Third parties we share data with</h2>
      <p>
        We share the minimum data required with the following service providers, each of which
        has its own privacy policy:
      </p>
      <ul>
        <li>
          <strong>Companies House</strong> – the filing itself (company details, accounts
          figures, authentication code at the moment of submission).
        </li>
        <li>
          <strong>Stripe</strong> – payment processing only (your email and the amount). Stripe
          handles your card details directly; we never see them.
        </li>
        <li>
          <strong>Resend</strong> – email delivery for your receipt and filing confirmation.
        </li>
        <li>
          <strong>Supabase</strong> – managed PostgreSQL hosting (EU region) for our database.
        </li>
      </ul>
      <p>
        We do not use advertising trackers, marketing analytics, or any data brokers.
      </p>

      <h2>7. Cookies and local storage</h2>
      <p>
        Our website does not use marketing or analytics cookies. We use local browser storage
        only to hold an admin authentication token (for staff using the admin dashboard) – this
        does not apply to customers using the public site.
      </p>

      <h2>8. How long we keep your data</h2>
      <p>
        We retain order and filing records for up to 6 years, in line with UK statutory
        requirements (Section 388 of the Companies Act 2006 and HMRC record-keeping rules).
        After that we delete them.
      </p>
      <p>
        Your authentication code is deleted within 30 minutes of submission (or sooner – usually
        immediately upon successful filing) regardless of the 6-year retention above.
      </p>

      <h2>9. Your rights (GDPR / UK GDPR)</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Ask for a copy of the personal data we hold about you.</li>
        <li>Ask us to correct any data that is inaccurate.</li>
        <li>
          Ask us to delete your data, subject to our legal obligation to retain filing records
          for the statutory period.
        </li>
        <li>Ask us to port your data to another service.</li>
        <li>Withdraw consent for the reminder email at any time.</li>
        <li>
          Complain to the UK Information Commissioner's Office (ICO) if you believe we've
          handled your data unlawfully – <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.
        </li>
      </ul>
      <p>
        To exercise any of these rights, email{' '}
        <a href="mailto:contact.dormantfiling@gmail.com">contact.dormantfiling@gmail.com</a>.
        We respond within 30 days.
      </p>

      <h2>10. Security</h2>
      <p>
        We take reasonable technical and organisational measures to protect your data:
        encryption in transit (TLS), encryption at rest, Row-Level Security on the database,
        rate limiting on our API, security headers, and the deliberate non-storage of your
        authentication code described above. No system is invulnerable; we aim to minimise both
        the amount of data we hold and the time we hold it for.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        If we make material changes to this policy, we'll update the "Last updated" date at the
        top and, where appropriate, notify users by email.
      </p>

      <h2>12. Contact</h2>
      <p>
        For any privacy enquiry, email{' '}
        <a href="mailto:contact.dormantfiling@gmail.com">contact.dormantfiling@gmail.com</a>.
        We'll get back to you within two working days for general questions, and within 30 days
        for formal GDPR requests.
      </p>

      <p className="mt-10 text-sm text-slate-500">
        See also our <Link to="/terms">Terms &amp; Conditions</Link>.
      </p>
    </LegalLayout>
  );
}
